// The list of part families. A family is the parts that stand in for each other
// in one spot on a drone. Boards get theirs derived at mint (item kind x platform
// generation: see item-identity.md "Families are derived"); people create the
// rest here, and the name opens the family's own page (PartFamilyPage), where its
// parts, name, description and the admin-only fields (Active, stale-box days)
// are edited.
//
// The server makes the key from the label and it never changes after.

import React, { useEffect, useState } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { canEditFamilies } from '../Parts/familyAccess';
import DrawerShell from '../Receiving/DrawerShell';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import { DataTable } from '../components/table/DataTable';
import { modalLabel } from '../components/review/fieldStyles';
import type { PartFamilyRow } from '../Receiving/types';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Field, TextArea } from '../components/ui/Field';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

const labelClass = modalLabel;

// ---------------------------------------------------------------------------
// The family table: the shared DataTable's columns.
//
// Module scope, per the DataTable contract. Everything the cells need beyond
// their own row travels on `meta` and is read at render time, and none of
// these cells may be wrapped in React.memo (it would skip the parent's render
// and read stale meta: the documented gotcha).
//
// No `sortable` and no accessorFn: the API hands families back in one order and
// nothing here has ever re-sorted them.
// ---------------------------------------------------------------------------

const FAMILY_COLUMNS: ColumnDef<PartFamilyRow, any>[] = [
  {
    id: 'key',
    header: 'Key',
    meta: { cellClass: 'font-mono text-xs text-on-surface-variant' },
    cell: ({ row }) => row.original.key,
  },
  {
    id: 'label',
    header: 'Label',
    cell: ({ row }) => (
      <Link
        to={`/admin/part-families/${row.original.id}`}
        className="rounded font-medium text-on-surface hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        {row.original.label}
      </Link>
    ),
  },
  {
    id: 'description',
    header: 'Description',
    // The td is the element that truncates, so the tooltip belongs on it too —
    // and jsdom folds `title` into an accessible name, which is why it stays
    // where it was rather than moving onto an inner span.
    meta: {
      cellClass: 'text-on-surface-variant max-w-md truncate',
      cellAttrs: (r: PartFamilyRow) => ({ title: r.description || '' }),
    },
    cell: ({ row }) => row.original.description || '—',
  },
  {
    id: 'stale_box_alert_days',
    header: 'Stale (days)',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.stale_box_alert_days ?? '—',
  },
  {
    id: 'is_active',
    header: 'Active',
    meta: { align: 'center' },
    cell: ({ row }) =>
      row.original.is_active ? (
        <span className="text-green-300">●</span>
      ) : (
        <span className="text-on-surface-disabled">○</span>
      ),
  },
];

const PartFamiliesAdmin: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);

  const [rows, setRows] = useState<PartFamilyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const history = useHistory();

  const fetchRows = async () => {
    setLoading(true);
    try {
      const data = (await api.get('/part-families')) as PartFamilyRow[];
      setRows(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!canEditFamilies(user)) return <Redirect to="/" />;

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        title="Part Families"
        helpTitle="About part families"
        lede="Parts that can stand in for each other in one spot on a drone."
        help={
          <>
            <p>
              A family is a set of parts that can stand in for each other in one spot on a
              drone. Stock counts as one pool, and a BOM line that names the family accepts
              any of its parts.
            </p>
            <p className="text-on-surface font-medium">Where families come from</p>
            <p>
              Minting a board finds or creates its family for you, from the item&apos;s
              kind and the drone generation it is for, so nobody seeds one by hand. Open a
              family to see its parts, add or remove them, rename it and describe it.
              Admins also set how many days a box of it may sit before it counts as stale,
              and retire one that is no longer used.
            </p>
          </>
        }
        primary={<PrimaryAction onClick={() => setCreating(true)}>+ New family</PrimaryAction>}
      />

      {/* The vessel is the shared table's. `emptyMode="replace"` keeps what the
          page had: the wait and the nothing-here line stand in for the whole
          section, header band included, rather than sitting under column
          headings that describe rows nobody has yet. */}
      <DataTable<PartFamilyRow>
        data={rows}
        columns={FAMILY_COLUMNS}
        getRowId={(r) => String(r.id)}
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading part families" className="space-y-1 py-6">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
          </SkeletonGroup>
        }
        empty={<EmptyState variant="panel">No families yet.</EmptyState>}
        emptyMode="replace"
      />

      {creating && (
        <FamilyForm
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            history.push(`/admin/part-families/${created.id}`);
          }}
        />
      )}
    </PageLayout>
  );
};

interface FormProps {
  onClose: () => void;
  onCreated: (created: PartFamilyRow) => void;
}

const FamilyForm: React.FC<FormProps> = ({ onClose, onCreated }) => {
  const api = useApi();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!label.trim()) {
      toast.error('Label is required');
      return;
    }
    setSubmitting(true);
    try {
      const created = (await api.post('/part-families', {
        body: {
          label: label.trim(),
          description: description.trim() || null,
        },
      })) as PartFamilyRow;
      toast.success(`Created family ${created.label}`);
      onCreated(created);
    } catch (err: any) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerShell title="New part family" onClose={onClose} dirty={label.trim() !== '' || description.trim() !== ''}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block">
          <span className={labelClass}>Label</span>
          <Field
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Gen 2 motor"
          />
        </label>
        <label className="block">
          <span className={labelClass}>Description</span>
          <TextArea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className={buttonClass('quiet')}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={buttonClass('filled')}
          >
            {submitting ? 'Saving…' : 'Create'}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
};

export default PartFamiliesAdmin;
